import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { SessionLanguage } from './contract'

/**
 * THE LOCALE — Mission B of the language design (`docs/specs/2026-08-14-the-language-design.md`
 * §5), built 2026-08-20. Phase 1 landed the server half in August: `Session.Language` from
 * creation through persistence to `/state`, mirrored into `contract.ts`, with the
 * `LanguageDirective` invariance seam behind it. The client half was scoped, given a mission
 * card, and never dispatched — so every session has been starting in the server default no
 * matter what the reader writes. Found by clicking through Hildur in Icelandic and being
 * answered in English.
 *
 * WHAT THIS OWNS, and the line is the spec's not mine: **the chrome only.**
 *
 *   > Boot choreography text and any diegetic string are DESIGNER-OWNED per language —
 *   > delivered as content, not translated inline by the builder.  (§5)
 *
 * So: buttons, labels, aria text, the sign-in door. NEVER a pack title, blurb, tag, opening
 * card, or any word the fiction speaks. Those arrive per language as content or not at all,
 * and a locale switch must not appear to translate them — the shelf showing Icelandic chrome
 * around English pack copy is the honest state, not a bug to paper over.
 *
 * ⚠ WHERE IT IS PERSISTED, and this is a deliberate deviation. §5 says "persisted per user",
 * which read strictly means an account column and a migration. This stores it in
 * localStorage — per browser, which for a single-device alpha is the same thing, and which
 * needs no migration and carries no deploy risk. The account-backed version is a small
 * follow-on the day someone reads on two devices; the seam below (`useLocale`) does not change
 * when it lands.
 *
 * ⚠ THE TRANSLATIONS ARE UNREVIEWED. Chrome translation is explicitly the builder's job here
 * ("enumerate and translate"), so these are written rather than stubbed — but the founder is
 * the native gate for `is` and their partner for `es`. Expect them to be edited; the keys are
 * what should stay still.
 */

export type Locale = SessionLanguage

export const LOCALES: readonly Locale[] = ['en', 'is', 'es', 'pl', 'no'] as const

/**
 * The endonym, always — a reader looking for their own language scans for the word they call
 * it, never for what English calls it. Not translated per locale for the same reason.
 *
 * `Norsk bokmål` rather than plain `Norsk`: Norway writes two standards, and a reader of
 * Nynorsk deserves to see at a glance that this is not theirs, rather than pick it and find out.
 */
export const LOCALE_NAME: Record<Locale, string> = {
  en: 'English',
  is: 'Íslenska',
  es: 'Español',
  pl: 'Polski',
  no: 'Norsk bokmål',
}

/**
 * The chrome dictionary. Flat keys, one object per locale — no nesting and no interpolation
 * library: there are a few dozen strings and the whole surface is deliberately sparse. Values
 * take `{named}` slots, filled by `t(key, vars)`.
 *
 * REGISTER: the English is lowercase and understated — "sign out", "send a link", "the list".
 * That is a design choice, not an accident, and the other two follow it. Sentence-case creeps
 * back in through translation if nobody says so, and it reads louder than this product is.
 */
const EN = {
  // --- the way in ---------------------------------------------------------
  'splash.continueWith': 'Continue with {provider}',
  'splash.opening': 'opening…',
  'splash.emailPlaceholder': 'your email',
  'splash.emailLabel': 'Your email',
  'splash.sendLink': 'send a link',
  'splash.orEmail': 'or use an email link',
  'splash.linkOnWay': 'a link is on its way to {email}.',
  'splash.notConfigured': 'sign-in is not configured here — this build runs open',
  'splash.doorDidNotOpen': 'That door did not open.',
  'splash.couldNotSend': 'Could not send the link.',
  'splash.wentWrong': 'Something went wrong. Try again in a moment.',

  // --- the shelf ----------------------------------------------------------
  'shelf.theList': 'the list',
  'shelf.sittings': 'sittings',
  'gate.notYet': 'this one opens when you are let in.',
  'shelf.wallet': 'wallet',
  'shelf.theme': 'appearance',
  'shelf.themeSystem': 'auto',
  'shelf.themeDark': 'dark',
  'shelf.themeLight': 'light',
  'shelf.signOut': 'sign out',

  /*
   * THE SHELF SECTIONS (2026-08-25 — the Talk-to-X wedge, Tier 0).
   *
   * Two groups because there are now two kinds of offer: a crossing into a place, and a short
   * framed conversation with somebody who holds. Selector.tsx draws neither heading until BOTH
   * groups have something in them — a label over the only section on the page is the furniture
   * the file's own comment refuses.
   *
   * ⚠ CHARACTERS → CONVERSATIONS → CHATS across two evenings (founder-ruled 2026-08-25). The
   * final rename carries a CONSTITUTION, not just a register: the category is humorous BY
   * DEFINITION — whimsical, funny, player-creative — and a serious messaging pack belongs under
   * some other shelf, never this one (a section is a promise about TONE AND SHAPE, never about
   * rendering surface — which is why messaging-surfaced Hildur sits under WORLDS, correctly).
   * *Chats* is the informal word for the informal promise; it is also the exact tab-word real
   * messaging apps use, which STRENGTHENS the this-is-just-texting license rather than cheapening
   * it. The doctrine-reserved "Conversations" (inhabit-format.md's roster) stays available for a
   * future serious lane if one ever ships.
   *
   * Written in caps against this dictionary's lowercase register because the value should be
   * what renders, rather than something the stylesheet has to shout for it.
   *
   * ⚠ `shelf.sectionWorlds` WAS NOT IN THE ORDER — the builder added it, because one labelled
   * group beside one unlabelled group reads as a mistake rather than as a distinction. If the
   * founder wants the worlds to stay the unnamed default, this key goes and Selector.tsx drops
   * the heading for that group only.
   *
   * ⚠ The four translations are the builder's, unreviewed, like the rest of the chrome.
   */
  'shelf.sectionWorlds': 'WORLDS',
  /*
   * ⚠ RENAMED FROM `CHATS` (founder-ruled 2026-09-02: "rename spjall to grín"). The shelf now
   * names WHAT IT PROMISES rather than the shape of the thing on it. Its constitution always
   * said so — CHATS promises humour by definition, and a reader who taps a row here must never
   * land in earnest — but the label said "chats", which is a form, and every other shelf on this
   * page is a form too (a chat, a discourse, a lesson, a world). A form-word could not tell
   * anybody why they would want this one.
   */
  'shelf.sectionChats': 'COMEDY',

  /*
   * THE THIRD SECTION — DISCOURSES (founder-commissioned 2026-08-26).
   *
   * ⚠ THE SECTION NAME IS THE FOUNDER'S AND IS THE ONE WORD THAT DOES ALL THE WORK HERE. The rows
   * under it render exactly like the CHATS rows, so this heading is carrying the whole distinction
   * between the two shelves, and the distinction is TONE: CHATS is light by definition, and this
   * is the sincere lane the note above reserved ("a future serious lane if one ever ships"). It
   * ships here — under a new word rather than under the doctrine-reserved "Conversations", which
   * names a shape and would say nothing about tone.
   *
   * ⚠ THE FOUR TRANSLATIONS ARE THE BUILDER'S AND UNREVIEWED, and each is the FORMAL word for a
   * dialogue in its own language: SAMRÆÐUR · DIÁLOGOS · DIALOGI · SAMTALER.
   *
   * ⚠ THE PAIRING THIS NOTE USED TO DESCRIBE IS GONE, and the axis moved with it (2026-09-02).
   * It read: SPJALL against SAMRÆÐUR, CHARLAS against DIÁLOGOS, POGAWĘDKI against DIALOGI, PRAT
   * against SAMTALER — each language saying the light thing and the serious thing with its own
   * two words. That was an INFORMAL-versus-FORMAL axis, and it described a register of speech
   * rather than what either shelf offers. With the first shelf renamed to GRÍN the axis is now
   * COMEDY against SINCERE, which is the distinction the two constitutions actually make and the
   * one a reader is choosing between. The formal words below are unaffected and still right;
   * what changed is that they are no longer the second half of a matched pair, and nothing about
   * this shelf now depends on what the other one is called.
   */
  'shelf.sectionDiscourses': 'DISCOURSES',

  /*
   * THE FOURTH SECTION — LEARNING (founder-commissioned 2026-08-28, the Learning avenue:
   * `docs/strategy/2026-08-28-the-learning-avenue.md`, direction ratified).
   *
   * Its constitution is SINCERE AND OVERT — the one shelf where the purpose may be said aloud:
   * a teacher is a teacher, the student came to learn, nobody pretends otherwise. What stays
   * unsaid is the machinery. Claims cap: a teacher, tuition — never certification, never
   * outcomes promised. The four translations below are the builder's, unreviewed, like the rest.
   */
  'shelf.sectionLearning': 'LEARNING',
  /*
   * THE SIXTH SECTION — ASSISTANTS (founder-commissioned 2026-09-02, the night the first one was
   * built). ⚠ BUILDER PLACEHOLDER, the founder's pen: the word is the roster thesis's own
   * (assistant-as-role, 2026-08-31) and says the offer plainly — a person whose one subject is
   * you. It does not say AI, on the wedge rule, and it must never grow a claim: an assistant,
   * a diary — never productivity, never coaching.
   */
  'shelf.sectionAssistants': 'ASSISTANTS',
  'shelf.sectionGames': 'GAMES',

  /*
   * A HELD CARD'S ONE WORD (2026-08-25) — see `Scenario.enterable`.
   *
   * It is what stops a greyed card reading as a broken one. Grey says "not available"; only this
   * says which kind of not-available it is, and announcing the pack is the reason it was left on
   * the shelf instead of switched off.
   *
   * ⚠ THE KEY IS NEUTRAL AND THE COPY IS NOT FINAL — the founder's pen. `notYet` rather than
   * `comingSoon` so a change of wording is a change of one string here and nothing else.
   *
   * ⚠ The four translations are the builder's, unreviewed, like the rest of the chrome.
   */
  'shelf.notYet': 'coming soon',

  /*
   * THE WAY BACK OUT OF A CONVERSATION opened from its own row (2026-08-25). It is the chevron's
   * ARIA label only — the control is a bare glyph, and this is what a reader using their ears
   * hears instead.
   *
   * ⚠ IT SAYS THE DIRECTION AND NOT THE DESTINATION, deliberately, where `corridor.back` says
   * "the corridor". The front page is the one screen this product refuses to name — Selector.tsx
   * carries the reasoning: "a wordmark above a grid of cards is self-evidently a shelf; naming it
   * would be the page explaining itself". Putting a name on it here, where only some readers hear
   * it, would be the worst of both.
   */
  'shelf.back': 'back',

  // --- the opening run ----------------------------------------------------
  'opening.run': 'the opening run',
  'opening.cardBefore': 'the card before',
  'opening.nextCard': 'the next card',
  'opening.cardCount': 'card {n} of {total}',

  // --- the thread ---------------------------------------------------------
  'thread.write': 'write',
  'thread.send': 'send',
  'thread.trouble': 'something went wrong',

  /*
   * ⚠ THE WORDMARK'S GLOSS — brand copy, the founder's pen, per language rather than chrome.
   *
   * "Old Norse" and not "Icelandic" (founder-ruled 2026-08-21). BOTH ARE ACCURATE: *heimar* is
   * the nominative plural of Old Norse *heimr* and of modern Icelandic *heimur* alike — the
   * singular drifted, the plural did not — so this is purely a question of which fact is worth
   * the line. Old Norse wins because the name was chosen for the COSMOLOGICAL sense, the plural
   * in Niflheimar · Jötunheimar · Álfheimar (CLAUDE.md). "Icelandic · worlds" says what the word
   * means; "Old Norse · worlds" says where it comes from, which is the question people actually
   * ask. One string to change if that judgement ever flips.
   *
   * ⚠ THE PARENTHESIS IS LOAD-BEARING, and the middle dot was wrong (founder, 2026-08-21).
   * `·` is a COORDINATING separator — peers in a list — and the shelf's own cards use it that
   * way directly below this ("messaging · Iceland · bench"). The relationship here is the other
   * kind: "worlds" is the meaning and "Old Norse" is the source OF it. One glyph doing two jobs
   * on one page is what gave it away. Meaning first, because a reader looking at the mark is
   * asking what it means before asking what language it is.
   *
   * ⚠ EMPTY IN ICELANDIC, deliberately. See Wordmark.tsx — the silence is the feature.
   */
  'mark.gloss': 'worlds',
  'mark.glossSource': 'Old Norse',

  // --- starting over (chrome: a control ABOUT the product, never in the world) ----------
  /*
   * THE DOORSTEP (2026-08-23) — asked once at the pack's door, and only when there is something
   * to resume. It replaced the standing *start over* control on the messaging grammar; the desk
   * keeps `reset.*`, whose reason for existing is its own `?session=` URL trap.
   * ⚠ The four translations are the builder's, like the rest of the chrome.
   */
  'thread.connecting': 'waiting for {name} to connect',
  // the bell (2026-09-14): his lines as notifications while the tab is not being looked at
  'thread.notifyOn': 'tell me when {name} writes',
  'thread.notifyOff': 'notifications on — press to stop',
  'resume.continue': 'continue',
  'resume.startNew': 'start again',
  'reset.start': 'start over',
  'reset.confirm': 'forget everything and start over? this cannot be undone',

  // --- the desk, chrome only (see DESK below for the rest) ----------------
  'desk.opening': 'opening the desk…',
  'desk.minutes': 'minutes',

  /*
   * THE CORRIDOR'S WORDS — the map's labels (hall brief §5b, authored 2026-08-22).
   *
   * ⚠ THESE MOVED OUT OF THE `DESK` BLOCK, and the move is the point. They lived there as
   * diegetic-and-untranslated because they came off an artboard and no pen had written them —
   * so all locales resolved to the same English by construction. §5b authored them in five
   * tongues, which makes them ordinary per-language content and the shared block the wrong home.
   *
   * They are the MAP's words, not the world's (hall brief §0 law 10): nothing in the fiction is
   * a corridor. Spanish keeps *la casa de Hildur* because *salón* is the beauty salon; Polish
   * *dwór* is the royal household as well as the manor, which is the sense that was ruled.
   *
   * ⚠ THE THREE WALL HEADINGS BELOW ARE UNUSED SINCE 2026-08-23 — the founder ruled the
   * corridor's headings off with the rest of the graphical round, and the walk's ORDER carries
   * what they said. Kept in all five languages rather than deleted: they are the pen's, five
   * translations are expensive to re-commission, and the sections may come back with an artist.
   * `corridor.home`, `corridor.writeTo` and `corridor.back` are all still live.
   */
  'corridor.valley': 'the way down the valley',
  'corridor.hall': "Hildur's hall",
  'corridor.farEnd': 'the far end',
  /*
   * TWO SECTIONS, AND UNA IS NOT ONE OF THE ROWS (founder-ruled 2026-08-23).
   *
   * Her only job is to find the right advisor from a general request, and you do not talk to her
   * on the regular — so listing her among the advisors would say she is one, and would leave a
   * standing invitation to a conversation nobody wants to have weekly. She is HOW YOU GET an
   * advisor, which is a tool rather than a relationship, so she sits in the section's own line.
   *
   * ⚠ It also solves day one, which is the case that decided it: with no advisor yet, a heading
   * over an empty list is a dead state — but a heading that offers the one thing you can do is
   * the empty state, correctly.
   *
   * ⚠ `corridor.askUna` LIVED HERE FOR AN HOUR AND IS GONE: the POC note (§1.2) put Una back as
   * the first row instead, which needs no control and no wording. The headings below are ⚠
   * unreviewed builder translations, like the rest of the chrome.
   */
  /*
   * ⚠  LIVED HERE BRIEFLY AND IS GONE (2026-08-23). Una's mark generalised
   * into a per-person TITLE the same evening — every name carries one — which means it is pack
   * CONTENT, authored per persona, not a chrome string. It travels on the wire as .
   */
  /*
   * ⚠ THE REALM (founder-ruled 2026-08-23). Una's row was deliberately unheaded — the POC note
   * §1.2 reasoned that a heading over her "would have to name a category she is the only member
   * of". She is not the only member once Hildur's own door is in the pack, so the objection
   * dissolved rather than being overruled, and the change fixes two orphans at once: her letters
   * had their own unheaded section at the foot of the list.
   *
   * ⚠ "the realm", not "Hildur's realm": the threshold the guest just pressed says *Enter
   * Hildur's realm*, so the heading pays that off instead of repeating her name directly above a
   * row that already carries it. Three plain nouns — the realm · home · advisors.
   * ⚠ Four translations are mine.
   */
  'corridor.sectionRealm': 'the realm',
  'corridor.sectionHome': 'home',
  'corridor.sectionAdvisors': 'advisors',
  'corridor.home': 'home',
  'corridor.writeTo': 'write to {name}',
  'corridor.back': 'the corridor',

  /*
   * ⚠ THE UNLOCKED DOOR (PARATEXT.md §1) — PARATEXT, NOT CHROME AND NOT FICTION.
   *
   * It sits in this dictionary because the label is the PRODUCT's, identical on every pack
   * ("the label never changes"), and authored per language rather than translated by a builder.
   * It is not in the DESK block: that block is diegetic strings kept deliberately untranslated,
   * and this is the opposite — the one link that is the product speaking outside the story.
   */
  /*
   * ⚠ UNUSED SINCE 2026-08-23 — addendum 21: *remove "the unlocked door" link from the bottom of
   * the page*. The standing link is off every surface; `/help` stays built, routed, gate-free and
   * request-free, reachable by URL. The label is kept in all five tongues, as the corridor's wall
   * headings are, because WHERE the door lives instead — behind her letter, on the site, a line
   * somewhere else — is the founder's and still open. The crisis floor never pointed here: it
   * carries the numbers in its own text, so its path is untouched.
   */
  'door.unlocked': 'the unlocked door',

  // --- the locale control itself ------------------------------------------
  'locale.label': 'language',
} as const

/**
 * ⚠ THE DESK'S DIEGETIC STRINGS — DESIGNER-OWNED, and identical in all three locales on
 * purpose. Do not "finish" this by translating it.
 *
 * §5's rule: *"Boot choreography text and any diegetic string are DESIGNER-OWNED per language —
 * delivered as content, not translated inline by the builder."* Enumerating the desk to comply
 * with that turned up something worth stating plainly: **almost the whole desk is diegetic.**
 * Its apparatus IS the fiction — "key the channel" is the Correspondent format's own verb,
 * "carrier / up" is the desk speaking, "given" and "received" are how a dossier is arranged.
 * A machine translation of any of them writes fiction in a language nobody has authored, and
 * would read as such to the one reader who matters.
 *
 * So they are KEYED but not TRANSLATED: every string is in one place and localizable the day
 * the desk's Icelandic register exists, and until then all three locales resolve to the same
 * English by construction — impossible to half-translate by accident.
 *
 * The aria labels ride along deliberately. A screen reader saying "open the dossier" over a
 * drawer headed *the dossier* must say the same words; splitting them would make the machine
 * voice and the glass disagree about what the thing is called.
 */
const DESK = {
  'desk.boot.aria': 'the desk is powering up',
  'desk.boot.power': 'desk power',
  'desk.boot.carrier': 'carrier',
  'desk.boot.up': 'up',
  'desk.boot.link': 'link',
  'desk.boot.skedStanding': 'sked standing',
  'desk.boot.noTraffic': 'no traffic',

  'desk.dossier': 'the dossier',
  'desk.dossier.open': 'open the dossier',
  'desk.dossier.close': 'close the dossier',
  'desk.dossier.empty': 'nothing on the desk yet.',
  'desk.dossier.given': 'given',
  'desk.dossier.received': 'received',
  'desk.dossier.unread': 'unread',

  'desk.book': 'the book',
  'desk.book.close': 'close the book',

  'desk.compose.key': 'key the channel',
  'desk.compose.keyAria': 'Key the channel',
  'desk.compose.answering': 'answering',
  'desk.traffic.transmitting': 'transmitting',
  'desk.instruments.aria': 'Desk instruments',

  'desk.arrangement.placeholder': 'what he works at',
  'desk.arrangement.unset': '— no arrangement — tap to set —',
  'desk.hoursPass': '— the hours pass —',
  // Notation rather than language: a clock format and an offset. Keyed for completeness so
  // nothing on the desk is hardcoded, but there is nothing here to translate.
  'desk.nextCall.placeholder': 'HH:MM or +40',
} as const

export type MessageKey = keyof typeof EN | keyof typeof DESK

const IS: Record<keyof typeof EN, string> = {
  'splash.continueWith': 'Halda áfram með {provider}',
  'splash.opening': 'opna…',
  'splash.emailPlaceholder': 'netfangið þitt',
  'splash.emailLabel': 'Netfangið þitt',
  'splash.sendLink': 'senda tengil',
  'splash.orEmail': 'eða fá tengil í tölvupósti',
  'splash.linkOnWay': 'tengill er á leiðinni til {email}.',
  'splash.notConfigured': 'innskráning er ekki uppsett hér — þessi útgáfa keyrir opin',
  'splash.doorDidNotOpen': 'Þessi leið opnaðist ekki.',
  'splash.couldNotSend': 'Ekki tókst að senda tengilinn.',
  'splash.wentWrong': 'Eitthvað fór úrskeiðis. Reyndu aftur eftir smástund.',

  'shelf.theList': 'listinn',
  'shelf.sittings': 'setur',
  'gate.notYet': 'þetta opnast þegar þú færð aðgang.',
  'shelf.wallet': 'veskið',
  'shelf.theme': 'útlit',
  'shelf.themeSystem': 'sjálfvirkt',
  'shelf.themeDark': 'dökkt',
  'shelf.themeLight': 'ljóst',
  'shelf.signOut': 'skrá út',
  /*
   * ⚠ BUILDER-TRANSLATED, and this one has a real choice in it: the Icelandic for "worlds" is
   * *heimar*, which is the product's own name — a section heading reading HEIMAR directly under
   * the Heimar wordmark says nothing. `VERALDIR` (plural of *veröld*) is the other true word for
   * it and is the one that can do a heading's job here. The founder is the native gate.
   */
  'shelf.sectionWorlds': 'VERALDIR',
  /* ⚠ FOUNDER'S WORD, and the source of the rename — `GRÍN`, 2026-09-02. It displaces the
     builder's `SPJALL` (the informal chat-word, *að spjalla*), which named the form; this names
     the promise. The one word on this shelf that is NOT builder-translated. */
  'shelf.sectionChats': 'GRÍN',
  /* ⚠ BUILDER-TRANSLATED. `SAMRÆÐUR` — the formal counterpart to SPJALL, and the word Icelandic
     uses for Plato's dialogues. The founder's native gate rules on it. */
  'shelf.sectionDiscourses': 'SAMRÆÐUR',
  /* ⚠ BUILDER-TRANSLATED. `NÁM` — the learner's own word (study/learning), short enough for a
     heading; KENNSLA (teaching) says the teacher's side and was passed over. The founder's
     native gate rules on it. */
  'shelf.sectionLearning': 'NÁM',
  /* ⚠ BUILDER-TRANSLATED. `AÐSTOÐ` — the plain word for assistance/help, short enough for a
     heading; AÐSTOÐARFÓLK (the people) is too long for the bar. The founder's native gate rules. */
  'shelf.sectionAssistants': 'AÐSTOÐ',
  'shelf.sectionGames': 'LEIKIR',
  'shelf.notYet': 'væntanlegt',
  'shelf.back': 'til baka',

  'opening.run': 'inngangurinn',
  'opening.cardBefore': 'fyrra spjaldið',
  'opening.nextCard': 'næsta spjald',
  'opening.cardCount': 'spjald {n} af {total}',

  'thread.write': 'skrifa',
  'thread.send': 'senda',
  'thread.trouble': 'eitthvað fór úrskeiðis',

  // Empty: their own word needs no gloss, and 'heimar · heimar' would be absurd.
  'mark.gloss': '',
  'mark.glossSource': '',

  'thread.connecting': 'beðið eftir að {name} tengist',
  'thread.notifyOn': 'láta mig vita þegar {name} skrifar',
  'thread.notifyOff': 'tilkynningar á — ýttu til að slökkva',
  'resume.continue': 'halda áfram',
  'resume.startNew': 'byrja upp á nýtt',
  'reset.start': 'byrja upp á nýtt',
  'reset.confirm': 'gleyma öllu og byrja upp á nýtt? þetta verður ekki tekið til baka',

  'desk.opening': 'opna skrifborðið…',
  'desk.minutes': 'mínútur',

  'door.unlocked': 'ólæstu dyrnar',

  'corridor.valley': 'leiðin niður í dalinn',
  'corridor.hall': 'höll Hildar',
  'corridor.farEnd': 'innst',
  'corridor.sectionRealm': 'ríkið',
  'corridor.sectionHome': 'heima',
  'corridor.sectionAdvisors': 'ráðgjafar',
  'corridor.home': 'heima',
  'corridor.writeTo': 'skrifa {name}',
  'corridor.back': 'gangurinn',

  'locale.label': 'tungumál',
}

const ES: Record<keyof typeof EN, string> = {
  'splash.continueWith': 'Continuar con {provider}',
  'splash.opening': 'abriendo…',
  'splash.emailPlaceholder': 'tu correo',
  'splash.emailLabel': 'Tu correo electrónico',
  'splash.sendLink': 'enviar un enlace',
  'splash.orEmail': 'o usa un enlace por correo',
  'splash.linkOnWay': 'va un enlace camino a {email}.',
  'splash.notConfigured': 'el inicio de sesión no está configurado aquí — esta versión corre abierta',
  'splash.doorDidNotOpen': 'Esa puerta no se abrió.',
  'splash.couldNotSend': 'No se pudo enviar el enlace.',
  'splash.wentWrong': 'Algo salió mal. Inténtalo de nuevo en un momento.',

  'shelf.theList': 'la lista',
  'shelf.sittings': 'sesiones',
  'gate.notYet': 'esto se abre cuando te dan acceso.',
  'shelf.wallet': 'cartera',
  'shelf.theme': 'apariencia',
  'shelf.themeSystem': 'automático',
  'shelf.themeDark': 'oscuro',
  'shelf.themeLight': 'claro',
  'shelf.signOut': 'cerrar sesión',
  /* ⚠ BUILDER-TRANSLATED, unreviewed. */
  'shelf.sectionWorlds': 'MUNDOS',
  /* ⚠ BUILDER-TRANSLATED, no gate on staff. `HUMOR` for GRÍN, displacing `CHARLAS`. `COMEDIA`
     was the alternative and was left: it names a genre of made thing — a film, a play — where
     HUMOR names the register, which is what this shelf actually promises. */
  'shelf.sectionChats': 'HUMOR',
  /* ⚠ BUILDER-TRANSLATED. `DIÁLOGOS` — formal where CHARLAS is informal; Plato's own word in
     Spanish. `DISCURSOS` was rejected: it means speeches, which is the opposite of this. */
  'shelf.sectionDiscourses': 'DIÁLOGOS',
  /* ⚠ BUILDER-TRANSLATED. `CLASES` — concrete and warm, the word a person actually uses
     ("clases de castellano"); APRENDIZAJE was rejected as abstract for a shelf. */
  'shelf.sectionLearning': 'CLASES',
  /* ⚠ BUILDER-TRANSLATED. `ASISTENTES` — the plain plural; the founder's gate rules. */
  'shelf.sectionAssistants': 'ASISTENTES',
  'shelf.sectionGames': 'JUEGOS',
  'shelf.notYet': 'próximamente',
  'shelf.back': 'volver',

  'opening.run': 'la apertura',
  'opening.cardBefore': 'la tarjeta anterior',
  'opening.nextCard': 'la tarjeta siguiente',
  'opening.cardCount': 'tarjeta {n} de {total}',

  'thread.write': 'escribe',
  'thread.send': 'enviar',
  'thread.trouble': 'algo salió mal',

  'mark.gloss': 'mundos',
  'mark.glossSource': 'nórdico antiguo',

  'thread.connecting': 'esperando a que {name} se conecte',
  'thread.notifyOn': 'avisarme cuando {name} escriba',
  'thread.notifyOff': 'avisos activados — pulsa para apagarlos',
  'resume.continue': 'continuar',
  'resume.startNew': 'empezar de nuevo',
  'reset.start': 'empezar de nuevo',
  'reset.confirm': '¿olvidarlo todo y empezar de nuevo? esto no se puede deshacer',

  'desk.opening': 'abriendo el escritorio…',
  'desk.minutes': 'minutos',

  'door.unlocked': 'la puerta sin llave',

  'corridor.valley': 'el camino al valle',
  'corridor.hall': 'la casa de Hildur',
  'corridor.farEnd': 'al fondo',
  'corridor.sectionRealm': 'el reino',
  'corridor.sectionHome': 'casa',
  'corridor.sectionAdvisors': 'consejeros',
  'corridor.home': 'casa',
  'corridor.writeTo': 'escribir a {name}',
  'corridor.back': 'el pasillo',

  'locale.label': 'idioma',
}

// The diegetic block is SPREAD INTO ALL THREE, not copied — so `is` and `es` cannot drift
// from English on a string nobody has authored, and the day one is authored it is one edit.
/**
 * POLSKI — added 2026-08-22 (founder). ⚠⚠ UNREVIEWED: written by the builder, as `is` and `es`
 * were, and awaiting a native reader. Register follows the English: lowercase, understated,
 * never sentence-case where a lower-case phrase will do.
 */
const PL: Record<keyof typeof EN, string> = {
  'splash.continueWith': 'Kontynuuj przez {provider}',
  'splash.opening': 'otwieranie…',
  'splash.emailPlaceholder': 'twój e-mail',
  'splash.emailLabel': 'Twój e-mail',
  'splash.sendLink': 'wyślij link',
  'splash.orEmail': 'albo użyj linku z e-maila',
  'splash.linkOnWay': 'link jest już w drodze na {email}.',
  'splash.notConfigured': 'logowanie nie jest tu skonfigurowane — ta wersja działa otwarcie',
  'splash.doorDidNotOpen': 'Te drzwi się nie otworzyły.',
  'splash.couldNotSend': 'Nie udało się wysłać linku.',
  'splash.wentWrong': 'Coś poszło nie tak. Spróbuj za chwilę.',

  'shelf.theList': 'lista',
  'shelf.sittings': 'sesje',
  'gate.notYet': 'to otworzy się, gdy dostaniesz dostęp.',
  'shelf.wallet': 'portfel',
  'shelf.theme': 'wygląd',
  'shelf.themeSystem': 'auto',
  'shelf.themeDark': 'ciemny',
  'shelf.themeLight': 'jasny',
  'shelf.signOut': 'wyloguj',
  /* ⚠ BUILDER-TRANSLATED, unreviewed. */
  'shelf.sectionWorlds': 'ŚWIATY',
  /* ⚠ BUILDER-TRANSLATED, the tester's gate. `HUMOR` for GRÍN, displacing `POGAWĘDKI`.
     `KOMEDIE` was rejected on the same ground as Spanish COMEDIA: it is the genre of a made
     thing, and this shelf is promising a register. */
  'shelf.sectionChats': 'HUMOR',
  /* ⚠ BUILDER-TRANSLATED. `DIALOGI` — the formal counterpart to POGAWĘDKI, and Plato's own word
     in Polish. `DYSKURSY` was rejected as academic jargon rather than a shelf word. */
  'shelf.sectionDiscourses': 'DIALOGI',
  /* ⚠ BUILDER-TRANSLATED. `NAUKA` — the everyday learning/study word. */
  'shelf.sectionLearning': 'NAUKA',
  /* ⚠ BUILDER-TRANSLATED. `ASYSTENCI` — the plain plural; a native gate rules. */
  'shelf.sectionAssistants': 'ASYSTENCI',
  'shelf.sectionGames': 'GRY',
  'shelf.notYet': 'wkrótce',
  'shelf.back': 'wróć',

  'opening.run': 'wprowadzenie',
  'opening.cardBefore': 'poprzednia karta',
  'opening.nextCard': 'następna karta',
  'opening.cardCount': 'karta {n} z {total}',

  'thread.write': 'napisz',
  'thread.send': 'wyślij',
  'thread.trouble': 'coś poszło nie tak',

  'mark.gloss': 'światy',
  'mark.glossSource': 'staronordyjski',

  'thread.connecting': 'oczekiwanie na połączenie z {name}',
  'thread.notifyOn': 'powiadom mnie, gdy {name} napisze',
  'thread.notifyOff': 'powiadomienia włączone — naciśnij, aby wyłączyć',
  'resume.continue': 'kontynuuj',
  'resume.startNew': 'zacznij od nowa',
  'reset.start': 'zacznij od nowa',
  'reset.confirm': 'zapomnieć wszystko i zacząć od nowa? tego nie da się cofnąć',

  'desk.opening': 'otwieranie biurka…',
  'desk.minutes': 'minuty',

  /* ⚠ DELIBERATELY ENGLISH. PARATEXT §1 authored this label in en/is/es only, and the page it
     opens exists in those three. Inventing a crisis-page label for a language whose crisis page
     is not written would promise a translation that is not there — the honest state is that both
     the link and the page are English until the pen writes them. */
  'door.unlocked': 'niezamknięte drzwi',

  'corridor.valley': 'droga w dół doliny',
  'corridor.hall': 'dwór Hildur',
  'corridor.farEnd': 'na końcu',
  'corridor.sectionRealm': 'królestwo',
  'corridor.sectionHome': 'dom',
  'corridor.sectionAdvisors': 'doradcy',
  'corridor.home': 'dom',
  'corridor.writeTo': 'napisz do {name}',
  'corridor.back': 'korytarz',

  'locale.label': 'język',
}

/**
 * NORSK BOKMÅL — added 2026-08-22 (founder). Native tester staffed.
 *
 * THE NORWEGIAN READING IS THE NAME WORKING, NOT BREAKING (founder, 2026-08-22 — correcting an
 * earlier note here that called it a collision). *Heimar* is the Nynorsk plural of *heim*, so a
 * Norwegian reader hears **"homes"** — and that is the doubleness the name already carries in
 * its own language: in Icelandic *heima* is home, and *heimar* means a world **and** a home at
 * once. It fits the platform exactly — somewhere you go, and somewhere that is yours.
 *
 * So the gloss below is not a correction of a misreading. It is one half of a pair, the same
 * half `worlds` is in English — and the other half is doing its own work unglossed, in Norwegian
 * more audibly than anywhere else.
 */
const NO: Record<keyof typeof EN, string> = {
  'splash.continueWith': 'Fortsett med {provider}',
  'splash.opening': 'åpner…',
  'splash.emailPlaceholder': 'e-posten din',
  'splash.emailLabel': 'E-posten din',
  'splash.sendLink': 'send en lenke',
  'splash.orEmail': 'eller få en lenke på e-post',
  'splash.linkOnWay': 'en lenke er på vei til {email}.',
  'splash.notConfigured': 'innlogging er ikke satt opp her — denne versjonen kjører åpen',
  'splash.doorDidNotOpen': 'Den døren åpnet seg ikke.',
  'splash.couldNotSend': 'Kunne ikke sende lenken.',
  'splash.wentWrong': 'Noe gikk galt. Prøv igjen om et øyeblikk.',

  'shelf.theList': 'listen',
  'shelf.sittings': 'økter',
  'gate.notYet': 'dette åpner når du slipper inn.',
  'shelf.wallet': 'lommebok',
  'shelf.theme': 'utseende',
  'shelf.themeSystem': 'auto',
  'shelf.themeDark': 'mørkt',
  'shelf.themeLight': 'lyst',
  'shelf.signOut': 'logg ut',
  /* ⚠ BUILDER-TRANSLATED, inside the founder's ear. `HUMOR` for GRÍN, displacing `PRAT` (the
     informal chat-word where SAMTALER was neutral). `KOMIKK` was left as the craft-word rather
     than the category-word — NRK files this register as Humor and so does everyone else. */
  'shelf.sectionWorlds': 'VERDENER',
  'shelf.sectionChats': 'HUMOR',
  /* ⚠ BUILDER-TRANSLATED. `SAMTALER` — the neutral-to-serious word the note above says PRAT
     displaced; it is free, and it is exactly the register this section wants. */
  'shelf.sectionDiscourses': 'SAMTALER',
  /* ⚠ BUILDER-TRANSLATED. `LÆRING` — the common modern word; OPPLÆRING reads as workplace
     training and was passed over. The founder rules. */
  'shelf.sectionLearning': 'LÆRING',
  /* ⚠ BUILDER-TRANSLATED. `ASSISTENTER` — the plain plural; the founder rules. */
  'shelf.sectionAssistants': 'ASSISTENTER',
  'shelf.sectionGames': 'SPILL',
  'shelf.notYet': 'kommer snart',
  'shelf.back': 'tilbake',

  'opening.run': 'innledningen',
  'opening.cardBefore': 'forrige kort',
  'opening.nextCard': 'neste kort',
  'opening.cardCount': 'kort {n} av {total}',

  'thread.write': 'skriv',
  'thread.send': 'send',
  'thread.trouble': 'noe gikk galt',

  'mark.gloss': 'verdener',
  'mark.glossSource': 'norrønt',

  'thread.connecting': 'venter på at {name} kobler til',
  'thread.notifyOn': 'si fra når {name} skriver',
  'thread.notifyOff': 'varsler på — trykk for å slå av',
  'resume.continue': 'fortsett',
  'resume.startNew': 'begynn på nytt',
  'reset.start': 'begynn på nytt',
  'reset.confirm': 'glemme alt og begynne på nytt? dette kan ikke angres',

  'desk.opening': 'åpner skrivebordet…',
  'desk.minutes': 'minutter',

  /* ⚠ DELIBERATELY ENGLISH. PARATEXT §1 authored this label in en/is/es only, and the page it
     opens exists in those three. Inventing a crisis-page label for a language whose crisis page
     is not written would promise a translation that is not there — the honest state is that both
     the link and the page are English until the pen writes them. */
  'door.unlocked': 'den ulåste døren',

  'corridor.valley': 'veien ned i dalen',
  'corridor.hall': 'Hildurs hall',
  'corridor.farEnd': 'innerst',
  'corridor.sectionRealm': 'riket',
  'corridor.sectionHome': 'hjem',
  'corridor.sectionAdvisors': 'rådgivere',
  'corridor.home': 'hjem',
  'corridor.writeTo': 'skriv til {name}',
  'corridor.back': 'gangen',

  'locale.label': 'språk',
}

const DICT: Record<Locale, Record<MessageKey, string>> = {
  en: { ...EN, ...DESK },
  is: { ...IS, ...DESK },
  es: { ...ES, ...DESK },
  pl: { ...PL, ...DESK },
  no: { ...NO, ...DESK },
}

const STORAGE_KEY = 'heimar.locale'

function isLocale(v: unknown): v is Locale {
  return typeof v === 'string' && (LOCALES as readonly string[]).includes(v)
}

/**
 * The first locale a reader gets, before they have ever chosen. Their browser's, when we speak
 * it — an Icelandic reader should not have to find a control to be addressed in Icelandic.
 * `navigator.languages` is ordered by preference, so the first match wins; English is the
 * fallback, not the default.
 *
 * ⚠ NOT FROM IP — considered and rejected (founder-ruled 2026-08-21), because it will read as an
 * obvious improvement to whoever comes next.
 *
 * Country is not language: one IP in Switzerland cannot choose between four national languages,
 * and the failure is worst for exactly the readers who care most — expats, students, travellers,
 * the diaspora. The founder is the case in point: an Icelander in Norway, whom geo-IP routes to
 * Norwegian and whose own browser says Icelandic.
 *
 * The deeper reason is the shape of the two signals. `Accept-Language` is the reader DECLARING a
 * preference; geo-IP is us INFERRING something about them from their network. Taking the
 * declaration over the inference is more accurate here and is also the only one of the two that
 * fits a product with no profiling in it — and it needs no third-party lookup, no request on the
 * critical path, and no location data to hold.
 *
 * IP belongs to currency, tax and jurisdiction. Not to this.
 *
 * ⚠ WHEN THE LIST GROWS: the two-character slice below is right for `is-IS` and `es-MX`, and
 * wrong the day regional variants differ (es-MX vs es-ES, pt-BR vs pt-PT). Prefer an exact tag
 * match before falling back to the base subtag.
 */
function detect(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isLocale(stored)) return stored
  } catch {
    /* private mode, or storage disabled — fall through to detection */
  }
  const prefs = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : []
  for (const tag of prefs) {
    const base = (tag ?? '').slice(0, 2).toLowerCase()
    if (isLocale(base)) return base
    if (base in ALIAS) return ALIAS[base]
  }
  return 'en'
}

/**
 * SUBTAGS THAT ARE NOT THE LOCALE'S OWN CODE.
 *
 * The two-character slice above is right until a language's browser tag differs from the code we
 * chose for it — which Norwegian does immediately: browsers send `nb-NO` for Bokmål, and `nb` is
 * not `no`, so without this a Norwegian reader's browser would be ignored and they would land in
 * English. Exactly the growth the detect() note warned about.
 *
 * ⚠ `nn` (Nynorsk) maps here TOO, and it is a compromise rather than a fact: Nynorsk is a
 * separate written language, not a variant, so this addresses those readers in the wrong one of
 * their two standards. It is still better than English — they read Bokmål daily — but the honest
 * fix is a `nn` locale with its own strings, and this line is where that decision will surface.
 */
const ALIAS: Record<string, Locale> = { nb: 'no', nn: 'no' }

interface LocaleContext {
  locale: Locale
  setLocale: (l: Locale) => void
  t: (key: MessageKey, vars?: Record<string, string | number>) => string
}

const Ctx = createContext<LocaleContext | null>(null)

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detect)

  // `lang` is not decoration: it drives hyphenation, quote shapes, and what a screen reader
  // sounds like. A page of Icelandic read aloud in an English voice is the accessibility
  // version of the bug this whole mission fixes.
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l)
    try {
      localStorage.setItem(STORAGE_KEY, l)
    } catch {
      /* the choice still holds for this visit */
    }
  }, [])

  const t = useCallback(
    (key: MessageKey, vars?: Record<string, string | number>) => {
      // Fall back to English per key, never per locale: a missing translation should show one
      // English word in place, not silently drop the reader's whole language.
      const raw = DICT[locale][key] ?? DICT.en[key] ?? key
      if (!vars) return raw
      return raw.replace(/\{(\w+)\}/g, (m, name) =>
        name in vars ? String(vars[name]) : m,
      )
    },
    [locale],
  )

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useLocale(): LocaleContext {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useLocale outside LocaleProvider')
  return ctx
}

/** Sugar for the common case. `const t = useT()` then `t('shelf.signOut')`. */
export function useT() {
  return useLocale().t
}
